import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Camera, Wand2, Save, X, Check, Loader2, ClipboardList } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { PrLinePicker } from '@/components/pr/PrLinePicker';
import { SearchSelect } from '@/components/po/SearchSelect';
import { VendorPicker } from '@/components/po/VendorPicker';
import { NO_SORT, sortChoices } from '@/components/po/sortChoices';
import { fmtQty, prApi, prErr } from '@/services/prApi';

const fromPrItem = ({ item, qty, sort }) => ({
  tempId: Math.random().toString(36).slice(2),
  pr_item_id: item.pr_item_id,
  pr_id: item.pr_id,
  pr_number: item.pr_number,
  pr_balance: item.balance_qty,
  supplier_design_name: sort?.supplier_design_name || item.design_name || '',
  mapped_sort_no: sort?.sort_no || '',
  mapped_fg_name: item.mapped_fg_name || '',
  ordered_qty: String(qty),
  unit: item.unit || 'pcs',
  rate: item.target_rate ? String(item.target_rate) : '',
  tax_pct: item.fg_tax_pct != null ? String(item.fg_tax_pct) : '5',
  taxLocked: item.fg_tax_pct != null,
  remarks: item.remarks || '',
  image_urls: item.image_urls || [],
  matching: false,
  matchResult: null,
});

const overBalance = (l) => l.pr_balance != null && Number(l.ordered_qty) > l.pr_balance + 1e-6;

const PrSourceTag = ({ l, idx }) => (l.pr_item_id ? (
  <span className="inline-flex items-center gap-1 rounded-md bg-indigo-50 px-2 py-0.5 text-[11px] text-indigo-700" data-testid={`line-pr-tag-${idx}`}>
    <ClipboardList className="h-3 w-3" /><span className="font-mono">{l.pr_number}</span>
    {l.pr_balance != null && <span className="text-indigo-500">· max {fmtQty(l.pr_balance)} {l.unit}</span>}
  </span>
) : <span className="text-[11px] italic text-gray-400" data-testid={`line-pr-tag-${idx}`}>Older line — not from a requisition</span>);

const NoLines = ({ onAdd }) => (
  <div className="rounded-lg border-2 border-dashed border-indigo-200 bg-indigo-50/40 px-4 py-10 text-center" data-testid="po-no-lines">
    <ClipboardList className="mx-auto mb-2 h-7 w-7 text-indigo-400" />
    <p className="text-sm font-medium text-gray-700">Every PO line comes from a Purchase Requisition</p>
    <p className="mb-3 text-xs text-gray-500">Pick the section, then add the items still to order from its open requisitions.</p>
    <Button onClick={onAdd} data-testid="add-from-pr-btn-empty"><Plus className="mr-1 h-4 w-4" />Add from requisition</Button>
  </div>
);

const POCreate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editPoId = searchParams.get('edit') || null;
  const isEditMode = Boolean(editPoId);
  const prParam = isEditMode ? null : searchParams.get('pr');
  const [picker, setPicker] = useState(null);

  const [sections, setSections] = useState([]);
  const [vendorCode, setVendorCode] = useState('');
  const [vendorInfo, setVendorInfo] = useState(null);
  const [vendorLocked, setVendorLocked] = useState(false); // cannot change vendor on edit
  const [sectionName, setSectionName] = useState('');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [lines, setLines] = useState([]);
  const [originalItemIds, setOriginalItemIds] = useState([]); // for delete diff on edit
  const [sortOptions, setSortOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(isEditMode);
  const [poNumber, setPoNumber] = useState('');

  useEffect(() => {
    apiClient.get('/purchase-orders/sections/list').then(r => setSections(r.data.sections || []));
  }, []);

  useEffect(() => {
    if (!prParam) return;
    prApi.get(prParam).then((pr) => {
      if (!['OPEN', 'PARTLY_ORDERED'].includes(pr.status)) { toast.error(`Requisition ${pr.pr_number} is not open for POs`); return; }
      setSectionName(pr.section_name);
      setPicker({ prId: pr.pr_id });
    }).catch((e) => toast.error(prErr(e, 'Could not load the requisition')));
  }, [prParam]);

  // Load existing PO when in edit mode
  useEffect(() => {
    if (!isEditMode) return;
    (async () => {
      try {
        const r = await apiClient.get(`/purchase-orders/${editPoId}`);
        const po = r.data;
        if (po.status !== 'DRAFT') {
          toast.error(`Cannot edit a ${po.status} PO`);
          navigate(`/po/${editPoId}`);
          return;
        }
        setPoNumber(po.po_number || '');
        setVendorCode(po.vendor_code || '');
        setVendorLocked(true);
        setSectionName(po.section_name || '');
        setExpectedDeliveryDate((po.expected_delivery_date || '').slice(0, 10));
        setRemarks(po.remarks || '');
        const items = (po.items || []).map(it => ({
          tempId: Math.random().toString(36).slice(2),
          po_item_id: it.po_item_id,
          pr_item_id: it.pr_item_id || null,
          pr_id: it.pr_id || null,
          pr_number: it.pr_number || null,
          supplier_design_name: it.supplier_design_name || '',
          mapped_sort_no: it.mapped_sort_no || '',
          mapped_fg_name: it.mapped_fg_name || '',
          ordered_qty: it.ordered_qty != null ? String(it.ordered_qty) : '',
          unit: it.unit || 'pcs',
          rate: it.rate != null ? String(it.rate) : '',
          tax_pct: it.tax_pct != null ? String(it.tax_pct) : '5',
          taxLocked: !!it.mapped_fg_name,
          remarks: it.remarks || '',
          image_urls: it.image_urls || [],
          matching: false,
          matchResult: null,
        }));
        const open = items.some(i => i.pr_item_id) ? await prApi.openItems({ section_name: po.section_name }).catch(() => null) : null;
        const left = Object.fromEntries((open || []).map(i => [i.pr_item_id, i.balance_qty]));
        setLines(open ? items.map(i => (i.pr_item_id ? { ...i, pr_balance: (left[i.pr_item_id] || 0) + Number(i.ordered_qty || 0) } : i)) : items);
        setOriginalItemIds(items.map(i => i.po_item_id).filter(Boolean));
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Failed to load PO');
        navigate('/po');
      } finally {
        setLoadingEdit(false);
      }
    })();
  }, [isEditMode, editPoId, navigate]);

  useEffect(() => {
    if (!vendorCode) { setSortOptions([]); return; }
    apiClient.get(`/purchase-orders/vendors/${vendorCode}/sort-nos`).then(r => setSortOptions(r.data.sort_nos || []));
  }, [vendorCode]);

  const updateLine = (idx, patch) => setLines(ls => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const pickSort = (idx, v) => {
    const l = lines[idx];
    if (v === NO_SORT) { updateLine(idx, { mapped_sort_no: '', ...(l.pr_item_id ? {} : { mapped_fg_name: '' }) }); return; }
    const s = sortOptions.find(x => x.sort_no === v);
    updateLine(idx, { mapped_sort_no: v, supplier_design_name: s?.supplier_design_name || l.supplier_design_name,
      mapped_fg_name: l.pr_item_id && l.mapped_fg_name ? l.mapped_fg_name : (s?.fg_name || '') });
  };
  const prLocked = lines.some(l => l.pr_item_id);
  const addLine = () => {
    if (!vendorCode) { toast.error('Select the vendor first — its sort nos are picked for each FG'); return; }
    if (!sectionName) { toast.error('Select the section first'); return; }
    setPicker({ prId: null });
  };
  const addPicked = (picks) => { setLines(ls => [...ls, ...picks.map(fromPrItem)]); setPicker(null); };
  const removeLine = (idx) => setLines(ls => ls.filter((_, i) => i !== idx));
  const igst = vendorInfo && vendorInfo.resolved_state && vendorInfo.resolved_state !== 'Tamil Nadu';

  const totals = lines.reduce((acc, l) => {
    const qty = Number(l.ordered_qty) || 0;
    const rate = Number(l.rate) || 0;
    const tax = Number(l.tax_pct) || 0;
    const base = qty * rate;
    const taxAmt = base * tax / 100;
    acc.subtotal += base;
    acc.tax += taxAmt;
    acc.total += base + taxAmt;
    return acc;
  }, { subtotal: 0, tax: 0, total: 0 });

  const handlePhotoUpload = async (idx, files) => {
    if (!files || files.length === 0) return;
    const fd = new FormData();
    for (const f of files) fd.append('files', f);
    try {
      const r = await apiClient.post('/purchase-orders/photos/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const newUrls = r.data.urls || [];
      updateLine(idx, { image_urls: [...lines[idx].image_urls, ...newUrls] });
      if ((r.data.errors || []).length) toast.warning(`Some uploads failed: ${r.data.errors.join('; ')}`);
      else toast.success(`${newUrls.length} photo(s) uploaded`);

      // Auto-trigger AI match on first uploaded photo if vendor is selected
      if (vendorCode && newUrls[0]) {
        runPhotoMatch(idx, newUrls[0]);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
  };

  const runPhotoMatch = async (idx, imageUrl) => {
    if (!vendorCode) { toast.error('Select a vendor first'); return; }
    updateLine(idx, { matching: true, matchResult: null });
    try {
      const r = await apiClient.post('/purchase-orders/photo-match', {
        vendor_code: vendorCode,
        image_url: imageUrl,
      });
      const res = r.data;
      if (res.matched) {
        updateLine(idx, {
          matching: false,
          matchResult: res,
          mapped_sort_no: res.sort_no,
          mapped_fg_name: res.fg_name,
        });
        toast.success(`Matched: ${res.sort_no} (${Math.round(res.confidence * 100)}%)`);
      } else {
        updateLine(idx, { matching: false, matchResult: res });
        toast.info('No match found — treating as new design');
      }
    } catch (e) {
      updateLine(idx, { matching: false });
      toast.error('Photo match failed');
    }
  };

  const removePhoto = (idx, url) => {
    updateLine(idx, { image_urls: lines[idx].image_urls.filter(u => u !== url) });
  };

  const handleSave = async () => {
    if (!vendorCode) return toast.error('Select vendor');
    if (!sectionName) return toast.error('Select section');
    if (!lines.length) return toast.error('Add at least one line from a requisition');
    if (lines.some(l => !l.supplier_design_name || !l.ordered_qty)) return toast.error('Fill supplier design + qty for all lines');
    const over = lines.find(overBalance);
    if (over) return toast.error(`${over.supplier_design_name}: only ${fmtQty(over.pr_balance)} ${over.unit} still to order on ${over.pr_number}`);
    setSaving(true);
    try {
      if (isEditMode) {
        // 1. Patch header (vendor cannot change post-create)
        await apiClient.patch(`/purchase-orders/${editPoId}`, {
          section_name: sectionName,
          expected_delivery_date: expectedDeliveryDate || null,
          remarks: remarks || '',
        });

        // 2. Delete removed items first (frees requisition qty), then update existing, then insert new
        const keptIds = new Set(lines.map(l => l.po_item_id).filter(Boolean));
        for (const origId of originalItemIds) {
          if (!keptIds.has(origId)) await apiClient.delete(`/purchase-orders/items/${origId}`);
        }
        for (const l of [...lines].sort((a, b) => Number(!a.po_item_id) - Number(!b.po_item_id))) {
          const payload = {
            supplier_design_name: l.supplier_design_name,
            mapped_sort_no: l.mapped_sort_no || null,
            mapped_fg_name: l.mapped_fg_name || null,
            ordered_qty: Number(l.ordered_qty) || 0,
            unit: l.unit,
            rate: Number(l.rate) || 0,
            tax_pct: Number(l.tax_pct) || 0,
            remarks: l.remarks || '',
            image_urls: l.image_urls || [],
          };
          if (l.po_item_id) {
            await apiClient.patch(`/purchase-orders/items/${l.po_item_id}`, payload);
          } else {
            await apiClient.post(`/purchase-orders/${editPoId}/items`, { ...payload, pr_item_id: l.pr_item_id });
          }
        }

        toast.success(`PO ${poNumber || editPoId} updated`);
        navigate(`/po/${editPoId}`);
      } else {
        const r = await apiClient.post('/purchase-orders', {
          vendor_code: vendorCode,
          section_name: sectionName,
          expected_delivery_date: expectedDeliveryDate || null,
          remarks,
          items: lines.map(l => ({
            supplier_design_name: l.supplier_design_name,
            mapped_sort_no: l.mapped_sort_no || null,
            mapped_fg_name: l.mapped_fg_name || null,
            ordered_qty: Number(l.ordered_qty) || 0,
            unit: l.unit,
            rate: Number(l.rate) || 0,
            tax_pct: Number(l.tax_pct) || 0,
            remarks: l.remarks,
            image_urls: l.image_urls,
            pr_item_id: l.pr_item_id,
          })),
        });
        toast.success(`PO ${r.data.po_number} created`);
        navigate(`/po/${r.data.po_id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || (isEditMode ? 'Failed to update PO' : 'Failed to create PO'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="po-create-page">
      <div className="bg-white border-b px-4 pb-3 pt-12 sm:pt-3 flex items-center gap-3 sticky top-0 z-10">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <h1 className="text-lg font-bold text-gray-900">{isEditMode ? `Edit Purchase Order${poNumber ? ` — ${poNumber}` : ''}` : 'Create Purchase Order'}</h1>
          <p className="text-xs text-gray-500">{isEditMode ? 'Editing DRAFT — changes save directly.' : 'Draft — will be saved as unissued.'}</p>
        </div>
        <Button onClick={handleSave} disabled={saving || loadingEdit} data-testid="save-po-btn"><Save className="w-4 h-4 mr-1" />{saving ? 'Saving...' : (isEditMode ? 'Save Changes' : 'Save Draft')}</Button>
      </div>

      {loadingEdit ? (
        <div className="max-w-6xl mx-auto p-10 flex flex-col items-center justify-center text-gray-500" data-testid="po-edit-loading">
          <Loader2 className="w-6 h-6 animate-spin mb-2" />
          Loading PO…
        </div>
      ) : (
      <div className="max-w-6xl mx-auto p-4 space-y-4">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Vendor & Section</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <VendorPicker vendorCode={vendorCode} onChange={setVendorCode} locked={vendorLocked} onVendor={setVendorInfo} />
            <div>
              <label className="text-xs text-gray-500 font-medium">Section *</label>
              <Select value={sectionName} onValueChange={setSectionName} disabled={prLocked}>
                <SelectTrigger data-testid="section-select"><SelectValue placeholder="Select section" /></SelectTrigger>
                <SelectContent>
                  {sectionName && !sections.some(s => s.section_name === sectionName) && <SelectItem value={sectionName}>{sectionName}</SelectItem>}
                  {sections.filter(s => s.section_name).map(s => <SelectItem key={s.section_id || s.section_name} value={s.section_name}>{s.section_name}</SelectItem>)}
                </SelectContent>
              </Select>
              {prLocked && <p className="text-[11px] text-gray-400 mt-1" data-testid="section-locked-note">Set by the requisition lines — remove them to change the section.</p>}
            </div>
            <div>
              <label className="text-xs text-gray-500 font-medium">Expected Delivery</label>
              <Input type="date" value={expectedDeliveryDate} onChange={(e) => setExpectedDeliveryDate(e.target.value)} data-testid="expected-delivery-input" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">Line Items</CardTitle>
            {lines.length > 0 && <Button size="sm" variant="outline" onClick={addLine} data-testid="add-from-pr-btn"><Plus className="w-3 h-3 mr-1" />Add from requisition</Button>}
          </CardHeader>
          <CardContent className="space-y-3">
            {lines.length === 0 && <NoLines onAdd={addLine} />}
            {lines.map((l, idx) => (
              <div key={l.tempId} className="border rounded-lg p-3 bg-white" data-testid={`line-${idx}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex flex-wrap items-center gap-2"><Badge variant="outline">Line {idx + 1}</Badge><PrSourceTag l={l} idx={idx} /></div>
                  <Button variant="ghost" size="sm" onClick={() => removeLine(idx)} className="text-red-500 h-7" data-testid={`remove-line-${idx}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                  <div className="md:col-span-4">
                    <label className="text-xs text-gray-500">Supplier Design *</label>
                    <Input value={l.supplier_design_name} onChange={(e) => updateLine(idx, { supplier_design_name: e.target.value })} placeholder="e.g., Lambore Jari 34567" data-testid={`supplier-design-${idx}`} />
                  </div>
                  <div className="md:col-span-4">
                    <label className="text-xs text-gray-500">Our Sort No{l.mapped_fg_name ? <span className="text-gray-400"> · FG {l.mapped_fg_name}</span> : ''}</label>
                    <SearchSelect value={l.mapped_sort_no || NO_SORT} onChange={(v) => pickSort(idx, v)} disabled={!vendorCode}
                      options={[...sortChoices(sortOptions, l.mapped_fg_name),
                        ...(l.mapped_sort_no && !sortOptions.some(s => s.sort_no === l.mapped_sort_no) ? [{ value: l.mapped_sort_no, label: l.mapped_sort_no, sub: 'not in this supplier\'s sort nos' }] : [])]}
                      placeholder={vendorCode ? 'Select sort no' : 'Pick vendor first'} searchPlaceholder="Search sort no, supplier design, FG…" testId={`sort-no-${idx}`} />
                    {l.mapped_sort_no && l.mapped_fg_name && (() => { const s = sortOptions.find(x => x.sort_no === l.mapped_sort_no); return s && s.fg_name && s.fg_name !== l.mapped_fg_name
                      ? <p className="mt-0.5 text-[11px] text-amber-700" data-testid={`sort-fg-mismatch-${idx}`}>Sort no {s.sort_no} is for FG {s.fg_name}</p> : null; })()}
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs text-gray-500">Qty *</label>
                    <Input type="number" min="0" max={l.pr_balance ?? undefined} value={l.ordered_qty} onChange={(e) => updateLine(idx, { ordered_qty: e.target.value })} className={overBalance(l) ? 'border-rose-400' : ''} data-testid={`qty-${idx}`} />
                    {overBalance(l) && <p className="mt-0.5 text-[11px] text-rose-600" data-testid={`qty-over-${idx}`}>Only {fmtQty(l.pr_balance)} still to order</p>}
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs text-gray-500">Unit</label>
                    <Select value={l.unit} onValueChange={(v) => updateLine(idx, { unit: v })} disabled={!!l.pr_item_id}>
                      <SelectTrigger data-testid={`unit-${idx}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pcs">pcs</SelectItem>
                        <SelectItem value="mtr">mtr</SelectItem>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="bundle">bundle</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-3">
                    <label className="text-xs text-gray-500">Rate</label>
                    <Input type="number" value={l.rate} onChange={(e) => updateLine(idx, { rate: e.target.value })} data-testid={`rate-${idx}`} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-xs text-gray-500">GST %{l.taxLocked && <span className="text-emerald-600"> · Rates Master</span>}</label>
                    <Input type="number" value={l.tax_pct} readOnly={l.taxLocked} onChange={(e) => updateLine(idx, { tax_pct: e.target.value })}
                      className={l.taxLocked ? 'bg-gray-50' : ''} data-testid={`tax-${idx}`} />
                    <p className="mt-0.5 text-[10px] text-gray-400" data-testid={`tax-split-${idx}`}>{igst ? `IGST ${Number(l.tax_pct) || 0}%` : `CGST ${(Number(l.tax_pct) || 0) / 2}% + SGST ${(Number(l.tax_pct) || 0) / 2}%`}</p>
                  </div>
                  <div className="md:col-span-7">
                    <label className="text-xs text-gray-500">Remarks</label>
                    <Input value={l.remarks} onChange={(e) => updateLine(idx, { remarks: e.target.value })} />
                  </div>
                </div>

                {/* Photo upload + AI match */}
                <div className="mt-3 pt-2 border-t">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <label className="cursor-pointer inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-dashed border-gray-300 hover:border-blue-500 text-xs">
                      <Camera className="w-3.5 h-3.5" />
                      <span>Click / Upload photo(s)</span>
                      <input type="file" accept="image/*" multiple capture="environment" className="hidden"
                        onChange={(e) => handlePhotoUpload(idx, e.target.files)}
                        data-testid={`photo-upload-${idx}`} />
                    </label>
                    {l.image_urls[0] && (
                      <Button size="sm" variant="outline" className="h-7 text-xs"
                        disabled={l.matching} onClick={() => runPhotoMatch(idx, l.image_urls[0])}
                        data-testid={`match-btn-${idx}`}>
                        <Wand2 className="w-3 h-3 mr-1" />{l.matching ? 'Matching...' : 'AI Match Design'}
                      </Button>
                    )}
                    {l.matchResult && (
                      l.matchResult.matched ? (
                        <Badge className="bg-green-100 text-green-700 border-green-300" variant="outline">
                          <Check className="w-3 h-3 mr-1" />Matched · {Math.round((l.matchResult.confidence || 0) * 100)}%
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">
                          New design (no vendor match)
                        </Badge>
                      )
                    )}
                  </div>
                  {l.image_urls.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {l.image_urls.map(u => (
                        <div key={u} className="relative group">
                          <img src={u} alt="design" className="w-20 h-20 object-cover rounded border" />
                          <button onClick={() => removePhoto(idx, u)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition">
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {lines.length > 0 && (
              <Button variant="outline" onClick={addLine} className="w-full border-dashed border-2 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 py-6" data-testid="add-from-pr-btn-bottom">
                <Plus className="w-4 h-4 mr-1" />Add more from requisition
              </Button>
            )}

            {/* Totals */}
            <div className="mt-3 bg-indigo-50 rounded-lg p-3 text-sm flex flex-wrap gap-6 justify-end" data-testid="po-totals">
              <div><span className="text-gray-500">Subtotal:</span> <span className="font-semibold">₹ {totals.subtotal.toFixed(2)}</span></div>
              {igst
                ? <div data-testid="po-total-igst"><span className="text-gray-500">IGST:</span> <span className="font-semibold">₹ {totals.tax.toFixed(2)}</span></div>
                : <>
                  <div data-testid="po-total-cgst"><span className="text-gray-500">CGST:</span> <span className="font-semibold">₹ {(totals.tax / 2).toFixed(2)}</span></div>
                  <div data-testid="po-total-sgst"><span className="text-gray-500">SGST:</span> <span className="font-semibold">₹ {(totals.tax / 2).toFixed(2)}</span></div>
                </>}
              <div className="text-base"><span className="text-gray-500">Grand Total:</span> <span className="font-bold text-indigo-700">₹ {totals.total.toFixed(2)}</span></div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Remarks</CardTitle></CardHeader>
          <CardContent>
            <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Optional notes for the supplier..." data-testid="po-remarks" />
          </CardContent>
        </Card>
      </div>
      )}

      <PrLinePicker open={!!picker} sectionName={sectionName} prId={picker?.prId} excludeIds={lines.map(l => l.pr_item_id).filter(Boolean)}
        sortOptions={sortOptions} onClose={() => setPicker(null)} onPick={addPicked} />
    </div>
  );
};

export default POCreate;
